import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AnyChatForm, AnyChatFormHeaderProps } from '@anyx/feature-crm/shared';

import { BlockTitle } from './block-title';
import { useLineMessageStore } from './store';

export interface LineMessageFormHeaderProps {
  title: string;
  hideBackBtn?: boolean;
  headerAside?: React.ReactNode;
  onLeavePage?: () => void;
}

export const LineMessageFormHeader = ({
  title,
  hideBackBtn,
  headerAside,
  onLeavePage,
}: LineMessageFormHeaderProps) => {
  const { t } = useTranslation('crm');

  const view = useLineMessageStore((store) => store.view);
  const navigateToDefaultView = useLineMessageStore((store) => store.navigateToDefaultView);
  const navigateToBlockView = useLineMessageStore((store) => store.navigateToBlockView);

  const breadcrumbs = useMemo(() => {
    const ret: AnyChatFormHeaderProps['breadcrumbs'] = [
      {
        label: title,
        onClick: navigateToDefaultView,
      },
    ];

    if (view.name !== 'DEFAULT') {
      ret.push({
        label: <BlockTitle type={view.name} withIcon={false} />,
        onClick: () => navigateToBlockView(view.name, view.blockIndex),
      });
    }

    if (view?.cardIndex !== undefined) {
      ret.push({
        label: t('crm.lineMessageForm.card'),
        onClick: () => navigateToBlockView(view.name, view.blockIndex, view.cardIndex),
      });
    }

    return ret;
  }, [
    navigateToBlockView,
    navigateToDefaultView,
    t,
    title,
    view.blockIndex,
    view.cardIndex,
    view.name,
  ]);

  const handleBack = () => {
    if (view.name === 'DEFAULT') {
      return onLeavePage?.();
    }

    if (view.cardIndex !== undefined) {
      return navigateToBlockView(view.name, view.blockIndex);
    }

    navigateToDefaultView();
  };

  return (
    <AnyChatForm.Header
      breadcrumbs={breadcrumbs}
      breadcrumbAside={headerAside}
      hideBackBtn={hideBackBtn}
      onBack={handleBack}
    />
  );
};
